import { getCurrencyCodeParam, getQueryParams } from './queryUtils';
import { isCurrencyCodeString, isCountryCodeString } from './currencyTypes';

// Unified type that works for both CHIRP V2 and REST (GMST) currency responses
// REST responses may have `null` for optional fields, CHIRP has `undefined`
// CHIRP types include symbols (unhandledEnumCase), REST uses strict enums
// Type guards handle both cases by filtering to defined string values
export const getCurrencyCodeUnit = ({
  countryCode,
  defaultCurrencyCode
}) => {
  if (countryCode !== 'US' && defaultCurrencyCode === 'USD') {
    return 'US$';
  }

  // Returned undefined to for proper type matching with the CurrencyStore type and FormattedIntlCurrency
  return undefined;
};
export const getShowCurrencyCodeUnit = ({
  currencyCode,
  currencyCodeUnit
}) => {
  // We don't want to show the currencyCodeUnit if it is undefined
  return currencyCode === 'USD' && !!currencyCodeUnit;
};
export const getValidatedCurrencyCodeParam = ({
  availableCurrencyCodes
}) => {
  const currencyCodeParam = getCurrencyCodeParam(getQueryParams(), availableCurrencyCodes);
  if (currencyCodeParam) {
    return currencyCodeParam.toUpperCase();
  }
  return null;
};
export const getDisplayedCurrencyCode = ({
  lockedCurrencyCode,
  defaultCurrencyCode,
  availableCurrencyCodes
}) => {
  const currencyCodeParam = getValidatedCurrencyCodeParam({
    availableCurrencyCodes
  });
  return lockedCurrencyCode || currencyCodeParam || defaultCurrencyCode;
};
export const getCurrencyStoreResponse = response => {
  const {
    countryCode,
    defaultCurrencyCode,
    availableCurrencyCodes
  } = response;
  // lockedCurrencyCode only exists on CurrencyResponse (in-app), not PublicCurrencyResponse
  const lockedCurrencyCode = 'lockedCurrencyCode' in response ? response.lockedCurrencyCode : undefined;

  // Validate before deriving anything from these values so a bad response
  // never produces an invalid `displayedCurrencyCode` downstream.
  if (!isCurrencyCodeString(defaultCurrencyCode)) {
    throw new Error(`Invalid currency response: defaultCurrencyCode is invalid (${String(defaultCurrencyCode)})`);
  }
  const filteredAvailableCurrencyCodes = availableCurrencyCodes.filter(isCurrencyCodeString);
  if (filteredAvailableCurrencyCodes.length === 0) {
    throw new Error('Invalid currency response: no valid currency codes available after filtering');
  }
  const currencyCodeUnit = getCurrencyCodeUnit({
    countryCode,
    defaultCurrencyCode
  });
  const showCurrencyCodeUnit = getShowCurrencyCodeUnit({
    currencyCode: defaultCurrencyCode,
    currencyCodeUnit
  });
  const displayedCurrencyCode = getDisplayedCurrencyCode({
    lockedCurrencyCode,
    defaultCurrencyCode,
    availableCurrencyCodes
  });
  return {
    currencyCodeUnit,
    showCurrencyCodeUnit,
    displayedCurrencyCode: isCurrencyCodeString(displayedCurrencyCode) ? displayedCurrencyCode : undefined,
    lockedCurrencyCode: isCurrencyCodeString(lockedCurrencyCode) ? lockedCurrencyCode : undefined,
    countryCode: isCountryCodeString(countryCode) ? countryCode : undefined,
    availableCurrencyCodes: filteredAvailableCurrencyCodes,
    defaultCurrencyCode
  };
};